import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, Unlock, AlertCircle, Loader2, CheckCircle2, Shield } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useEffect as useLayoutEffect } from 'react';

// Enhanced confetti burst component with better performance
const ConfettiBurst = () => (
  <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
    {[...Array(25)].map((_, i) => (
      <motion.span
        key={i}
        className="absolute text-2xl"
        initial={{ 
          opacity: 0, 
          scale: 0,
          x: 0,
          y: 0
        }}
        animate={{ 
          opacity: [0, 1, 0],
          scale: [0, 1, 0.8],
          x: Math.random() * 200 - 100,
          y: Math.random() * 200 - 100
        }}
        transition={{ 
          duration: 2,
          delay: i * 0.05,
          ease: "easeOut"
        }}
        style={{
          color: ["#10B981", "#3B82F6", "#F59E42", "#8B5CF6", "#EF4444", "#06B6D4", "#84CC16"][i % 7],
        }}
      >
        {['🎉','✨','🟢','🔵','🟣','🟠','🔴','⭐','💫','🌟'][i % 10]}
      </motion.span>
    ))}
  </div>
);

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const { login, isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(email));
  }, [email]);

  useEffect(() => {
    setIsValidPassword(password.length >0);
  }, [password]);

  // Account lockout after 5 failed attempts
  useEffect(() => {
    if (attempts >= 5) {
      setIsLocked(true);
      setTimeout(() => {
        setIsLocked(false);
        setAttempts(0);
      }, 300000); // 5 minutes
    }
  }, [attempts]);

  // Add global style for custom selection color
  useLayoutEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      ::selection { background: #3B82F6; color: #fff; }
      input::selection, textarea::selection { background: #3B82F6; color: #fff; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      await login(email, password);
      setShowSuccess(true);
      setAttempts(0);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
      navigate('/');
    } catch (error) {
      setAttempts(prev => prev + 1);
      const remainingAttempts = 5 - attempts - 1;
      if (remainingAttempts > 0) {
        setError(`Invalid email or password. ${remainingAttempts} attempts remaining.`);
      } else {
        setError('Account temporarily locked. Please try again in 5 minutes.');
      }
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render login form if user is already authenticated
  if (isAuthenticated && user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-100 via-white to-indigo-100 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Enhanced background elements */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-200 opacity-20 rounded-full z-0 animate-pulse" />
      <div className="absolute top-1/2 -right-40 w-[28rem] h-[28rem] bg-purple-200 opacity-15 rounded-full z-0 animate-pulse" style={{ transform: 'translateY(-50%)', animationDelay: '1s' }} />
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-200 opacity-10 rounded-full z-0 animate-pulse" style={{ transform: 'translateX(-50%)', animationDelay: '2s' }} />
      
      {/* Enhanced radial gradient overlay */}
      <div className="pointer-events-none absolute inset-0 z-0" style={{ background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.3) 60%, transparent 100%)' }} />
      
      {/* Enhanced animated SVG background */}
      <svg className="absolute inset-0 w-full h-full z-0" style={{ pointerEvents: 'none' }} aria-hidden="true">
        <motion.circle 
          cx="20%" cy="30%" r="80" fill="#3B82F6" fillOpacity="0.08"
          animate={{ r: [80, 90, 80] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.circle 
          cx="80%" cy="70%" r="100" fill="#10B981" fillOpacity="0.07"
          animate={{ r: [100, 110, 100] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.circle 
          cx="60%" cy="20%" r="60" fill="#F59E42" fillOpacity="0.06"
          animate={{ r: [60, 70, 60] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.circle 
          cx="50%" cy="80%" r="90" fill="#8B5CF6" fillOpacity="0.06"
          animate={{ r: [90, 100, 90] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
      </svg>
      
      {/* Enhanced floating animated icons */}
      <div className="pointer-events-none absolute z-20 w-full h-full">
        <motion.span 
          className="absolute left-10 top-24 text-3xl opacity-30"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          💡
        </motion.span>
        <motion.span 
          className="absolute right-12 top-40 text-2xl opacity-30"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        >
          🚀
        </motion.span>
        <motion.span 
          className="absolute left-1/2 bottom-20 text-2xl opacity-20"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          🌟
        </motion.span>
        <motion.span 
          className="absolute right-1/4 top-1/3 text-xl opacity-25"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        >
          ✨
        </motion.span>
      </div>
      
      {/* Confetti burst on success */}
      {showSuccess && <ConfettiBurst />}
      
      <div className="flex-1 flex flex-col justify-center items-center w-full z-10 px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8"
        >
          <motion.div 
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.div
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                className="mx-auto h-16 sm:h-20 lg:h-24 w-auto transition-transform duration-300 drop-shadow-xl"
                src="https://dvijinfotech.com/wp-content/uploads/2024/11/logo-header.svg"
                alt="Dvij Infotech"
              />
            </motion.div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl backdrop-blur-sm"
          >
            <div className="space-y-7">
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-700">{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                )}
                
                {isLocked && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="bg-orange-50 border border-orange-200 rounded-lg p-4"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-orange-600" />
                      <span className="text-orange-700 text-sm">
                        Account temporarily locked. Please wait 5 minutes before trying again.
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-7" aria-label="Login form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Address
                  </Label>
                  <div className="relative group">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      aria-label="Email address"
                      autoComplete="username"
                      disabled={isLocked}
                      className={cn(
                        "pl-12 h-10 sm:h-12 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none bg-white text-gray-800 placeholder-gray-400 transition-all duration-200 text-sm sm:text-base",
                        isValidEmail && "border-green-400 focus:border-green-400 focus:ring-green-100",
                        isLocked && "opacity-50 cursor-not-allowed"
                      )}
                    />
                    {isValidEmail && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute right-3 top-1/3 transform -translate-y-1/2"
                      >
                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                      </motion.div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Password
                  </Label>
                  <div className="relative group">
                    {isValidPassword ? (
                      <Unlock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-400 group-focus-within:text-blue-400 transition-colors" />
                    ) : (
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" />
                    )}
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      aria-label="Password"
                      autoComplete="current-password"
                      disabled={isLocked}
                      className={cn(
                        "pl-12 h-12 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none bg-white text-gray-800 placeholder-gray-400 transition-all duration-200",
                        isValidPassword && "pr-16 border-green-400 focus:border-green-400 focus:ring-green-100",
                        "pr-12",
                        isLocked && "opacity-50 cursor-not-allowed"
                      )}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                      <button
                        type="button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        disabled={isLocked}
                        onClick={(e) => {
                          if (isLocked) return;
                          setShowPassword(!showPassword);
                          // Enhanced ripple effect
                          const btn = e.currentTarget;
                          const ripple = document.createElement('span');
                          ripple.className = 'ripple';
                          ripple.style.position = 'absolute';
                          ripple.style.left = '50%';
                          ripple.style.top = '50%';
                          ripple.style.transform = 'translate(-50%, -50%)';
                          ripple.style.width = ripple.style.height = '36px';
                          ripple.style.background = 'rgba(59,130,246,0.2)';
                          ripple.style.borderRadius = '50%';
                          ripple.style.pointerEvents = 'none';
                          ripple.style.zIndex = '10';
                          ripple.style.animation = 'ripple-anim 0.5s linear';
                          btn.appendChild(ripple);
                          setTimeout(() => btn.removeChild(ripple), 500);
                        }}
                        className={cn(
                          "text-gray-400 hover:text-gray-600 transition-colors focus:outline-none relative overflow-hidden",
                          isLocked && "opacity-50 cursor-not-allowed"
                        )}
                        style={{ position: 'relative' }}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                      {isValidPassword && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          <CheckCircle2 className="h-5 w-5 text-green-400" />
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-white font-semibold text-base shadow-md focus:outline-none transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#21a9e2' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2A3C91';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#21a9e2';
                  }}
                  disabled={isLoading || !isValidEmail || !isValidPassword || isLocked}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin h-5 w-5" />
                      <span>Signing In...</span>
                    </>
                  ) : (
                    <>
                      
                      <span>Sign In</span>
                    </>
                  )}
                </Button>
                
                {showSuccess && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center gap-2 text-green-600 font-medium mt-2"
                  >
                    <CheckCircle2 className="h-5 w-5" /> Login successful!
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      <footer className="w-full text-center py-4 text-sm text-gray-900 mt-8">
        &copy; {new Date().getFullYear()} Dvij Infotech. All rights reserved.
      </footer>
      
      {/* Enhanced ripple animation keyframes */}
      <style>{`
        @keyframes ripple-anim {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(0.2); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); }
        }
        
        input:focus, input:focus-visible, input:active {
          outline: none !important;
          box-shadow: none !important;
        }
        
        /* Ensure crisp text rendering */
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        
        /* Prevent text blur on transforms */
        .transform, .motion-safe\\:transform {
          backface-visibility: hidden;
          transform-style: preserve-3d;
        }
        
        /* Enhanced button hover effects */
        button:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        
        button:not(:disabled):active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
        }
        
        /* Remove transitions from footer */
        footer {
          transition: none !important;
        }
        
        footer * {
          transition: none !important;
        }
      `}</style>
    </div>
  );
} 